/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, useEffect } from "react";
import { Control, useController, FieldValues } from "react-hook-form";
import {
  HierarchicalMultiSelectWrapper,
  SelectButton,
  SelectedText,
  DropdownContainer,
  SelectPanel,
  BackButton,
  PanelList,
  ItemLabel,
  NoResults,
  IconWrapper,
} from "./styles";
import { Input, IconSearch } from "tvs-unified-lib";
import { useLocationMaster } from "@/services/hooks/customer-params/useCustomerParams";
import IconMultilevelArrow from "./icon-multilevel-select-arrow.svg?react";

export interface LocationOption {
  label: string;
  value: string;
  children?: LocationOption[];
  data?: any;
}

interface LocationHierarchySelectProps<
  TFieldValues extends FieldValues = FieldValues
> {
  control: Control<any | TFieldValues>;
  name: string;
  onChange?: (value: LocationOption[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  formLabel?: string;
  isRequired?: boolean;
}

const transformLocationToOptions = (data: any[]): LocationOption[] => {
  return (
    data?.map((item) => ({
      label: item.name || item.pincode,
      value: item.id.toString(),
      data: item,
    })) || []
  );
};

const LocationHierarchySelect = <
  TFieldValues extends FieldValues = FieldValues
>({
  control,
  name,
  onChange: onChangeProp,
  placeholder = "Select location...",
  className = "",
  disabled = false,
  formLabel = "",
  isRequired = false,
}: LocationHierarchySelectProps<TFieldValues>) => {
  const {
    field: { value: formValue = [], onChange: onChangeField },
    fieldState: { error },
  } = useController({ name, control });

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const loadMoreRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStateIds, setSelectedStateIds] = useState<string[]>([]);
  const [selectedCityIds, setSelectedCityIds] = useState<string[]>([]);

  const {
    data: statesData,
    isLoading: isLoadingStates,
    hasNextPage: hasNextStatesPage,
    fetchNextPage: fetchNextStatesPage,
    isFetchingNextPage: isFetchingNextStatesPage,
  } = useLocationMaster(
    { type: "state", limit: 10, search: searchTerm },
    { enabled: isOpen }
  );
  const stateOptions = useMemo(
    () => transformLocationToOptions(statesData),
    [statesData]
  );

  const {
    data: citiesData,
    isLoading: isLoadingCities,
    hasNextPage: hasNextCitiesPage,
    fetchNextPage: fetchNextCitiesPage,
    isFetchingNextPage: isFetchingNextCitiesPage,
  } = useLocationMaster(
    {
      type: "city",
      parent_id: selectedStateIds,
      limit: 10,
      search: searchTerm,
    },
    { enabled: isOpen && selectedStateIds.length > 0 }
  );
  const cityOptions = useMemo(
    () => transformLocationToOptions(citiesData),
    [citiesData]
  );

  const {
    data: areasData,
    isLoading: isLoadingAreas,
    hasNextPage: hasNextAreasPage,
    fetchNextPage: fetchNextAreasPage,
    isFetchingNextPage: isFetchingNextAreasPage,
  } = useLocationMaster(
    {
      type: "area",
      parent_id: selectedCityIds,
      limit: 10,
      search: searchTerm,
    },
    { enabled: isOpen && selectedCityIds.length > 0 }
  );
  const areaOptions = useMemo(
    () => transformLocationToOptions(areasData),
    [areasData]
  );

  const handleCheckboxClick = (
    e: React.ChangeEvent<HTMLInputElement>,
    item: LocationOption,
    level: number
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (level === 1) {
      setSelectedStateIds([item.value]);
    } else if (level === 2) {
      setSelectedCityIds([item.value]);
    }
  };

  const handleItemClick = (
    e: React.MouseEvent,
    item: LocationOption,
    level: number
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (level === 1) {
      setSelectedStateIds([item.value]);
      setCurrentLevel(2);
      setSearchTerm("");
    } else if (level === 2) {
      setSelectedCityIds([item.value]);
      setCurrentLevel(3);
      setSearchTerm("");
    } else if (level === 3) {
      const state = stateOptions.find((s) => s.value === selectedStateIds[0]);
      const city = cityOptions.find((c) => c.value === selectedCityIds[0]);
      const newSelection = {
        label: `${state?.label}, ${city?.label}, ${item.label}`,
        value: item.value,
        data: {
          state: state?.data,
          city: city?.data,
          area: item.data,
        },
      };

      onChangeField([newSelection]);
      onChangeProp?.([newSelection]);
      setIsOpen(false);
    }
  };

  const handleBackClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (currentLevel === 3) {
      setCurrentLevel(2);
      setSearchTerm("");
    } else if (currentLevel === 2) {
      setCurrentLevel(1);
      setSearchTerm("");
    }
  };

  const handleReset = () => {
    setSelectedStateIds([]);
    setSelectedCityIds([]);
    onChangeField([]);
    onChangeProp?.([]);
    setCurrentLevel(1);
    setSearchTerm("");
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const getHasNextPage = () => {
    switch (currentLevel) {
      case 1:
        return hasNextStatesPage;
      case 2:
        return hasNextCitiesPage;
      case 3:
        return hasNextAreasPage;
      default:
        return false;
    }
  };

  const getFetchNextPage = () => {
    switch (currentLevel) {
      case 1:
        return fetchNextStatesPage;
      case 2:
        return fetchNextCitiesPage;
      case 3:
        return fetchNextAreasPage;
      default:
        return () => {};
    }
  };

  const getIsFetchingNextPage = () => {
    switch (currentLevel) {
      case 1:
        return isFetchingNextStatesPage;
      case 2:
        return isFetchingNextCitiesPage;
      case 3:
        return isFetchingNextAreasPage;
      default:
        return false;
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          getHasNextPage() &&
          !getIsFetchingNextPage()
        ) {
          getFetchNextPage()();
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [currentLevel, getHasNextPage, getIsFetchingNextPage, getFetchNextPage]);

  useEffect(() => {
    if (isOpen) {
      const panelList = document.querySelector(
        ".theme-multilevel-select-panel-list"
      );
      if (panelList) {
        panelList.scrollTop = 0;
      }
    }
  }, [currentLevel, isOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const panelList = document.querySelector(
        ".theme-multilevel-select-panel-list"
      );
      if (panelList) {
        const { scrollTop, scrollHeight, clientHeight } = panelList;
        if (
          scrollHeight - scrollTop - clientHeight < 300 &&
          getHasNextPage() &&
          !getIsFetchingNextPage()
        ) {
          getFetchNextPage()();
        }
      }
    };

    const panelList = document.querySelector(
      ".theme-multilevel-select-panel-list"
    );
    if (panelList) {
      panelList.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (panelList) {
        panelList.removeEventListener("scroll", handleScroll);
      }
    };
  }, [currentLevel, getHasNextPage, getIsFetchingNextPage, getFetchNextPage]);

  const getCurrentLevelData = () => {
    switch (currentLevel) {
      case 1:
        return stateOptions;
      case 2:
        return cityOptions;
      case 3:
        return areaOptions;
      default:
        return [];
    }
  };

  const isLoading = isLoadingStates || isLoadingCities || isLoadingAreas;

  const filteredItems = getCurrentLevelData().filter((item) =>
    item.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayLabel =
    formValue.length > 0
      ? formValue.map((v) => v.label).join("; ")
      : placeholder;

  const isItemSelected = (item: LocationOption, level: number) => {
    if (level === 1) {
      return (
        selectedStateIds.includes(item.value) ||
        formValue.some((v) => v.data?.state?.id?.toString() === item.value)
      );
    } else if (level === 2) {
      return (
        selectedCityIds.includes(item.value) ||
        formValue.some((v) => v.data?.city?.id?.toString() === item.value)
      );
    } else if (level === 3) {
      return formValue.some(
        (v) =>
          v.value === item.value || v.data?.area?.id?.toString() === item.value
      );
    }
    return false;
  };

  return (
    <HierarchicalMultiSelectWrapper className={className} ref={wrapperRef}>
      {formLabel && (
        <label className="theme-form-input-label">
          {formLabel}{" "}
          {isRequired && <span className="color-theme-red-500">*</span>}{" "}
        </label>
      )}

      <SelectButton
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`${error ? "has-error" : ""} ${disabled ? "disabled" : ""}`}
        disabled={disabled}
      >
        <SelectedText>{displayLabel}</SelectedText>
        <IconWrapper>
          <svg
            fill="currentColor"
            viewBox="0 0 40 40"
            style={{
              transform: isOpen ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.2s ease",
            }}
          >
            <path d="M31 26.4q0 .3-.2.5l-1.1 1.2q-.3.2-.6.2t-.5-.2l-8.7-8.8-8.8 8.8q-.2.2-.5.2t-.5-.2l-1.2-1.2q-.2-.2-.2-.5t.2-.5l10.4-10.4q.3-.2.6-.2t.5.2l10.4 10.4q.2.2.2.5z"></path>
          </svg>
        </IconWrapper>
      </SelectButton>

      {isOpen && (
        <DropdownContainer>
          <SelectPanel>
            <div className="theme-multilevel-select-option theme-multilevel-select-search">
              <Input
                name={`${name}-search`}
                placeholder="Search..."
                value={searchTerm}
                onInputChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                icon={<IconSearch />}
                formLabel=""
              />
            </div>

            {currentLevel === 1 && (
              <div
                className={`theme-multilevel-select-option`}
                onClick={handleReset}
              >
                <span className="color-theme-red-500">Reset Selection</span>
              </div>
            )}

            {currentLevel > 1 && (
              <BackButton onClick={handleBackClick}>
                <div className="theme-multilevel-arrow">
                  <IconMultilevelArrow />
                </div>
                <span>
                  {currentLevel === 3
                    ? cityOptions.find((c) => c.value === selectedCityIds[0])
                        ?.label
                    : currentLevel === 2
                    ? stateOptions.find((s) => s.value === selectedStateIds[0])
                        ?.label
                    : ""}
                </span>
              </BackButton>
            )}

            <PanelList>
              {isLoading ? (
                <NoResults>Loading...</NoResults>
              ) : filteredItems.length === 0 ? (
                <NoResults>No results found</NoResults>
              ) : (
                <>
                  {filteredItems.map((item) => (
                    <div
                      className="theme-multilevel-select-option"
                      key={item.value}
                      onClick={(e) => handleItemClick(e, item, currentLevel)}
                    >
                      <ItemLabel>
                        <input
                          type="checkbox"
                          aria-label={`Select all under ${item.label}`}
                          checked={isItemSelected(item, currentLevel)}
                          onChange={(e) =>
                            handleCheckboxClick(e, item, currentLevel)
                          }
                        />
                        <span>{item.label}</span>
                      </ItemLabel>
                      {currentLevel < 3 && (
                        <div className="theme-multilevel-arrow-right">
                          <IconMultilevelArrow />
                        </div>
                      )}
                    </div>
                  ))}
                  {getHasNextPage() && (
                    <div
                      ref={loadMoreRef}
                      style={{ height: "20px", width: "100%" }}
                    >
                      {getIsFetchingNextPage() && (
                        <NoResults>Loading more...</NoResults>
                      )}
                    </div>
                  )}
                </>
              )}
            </PanelList>
          </SelectPanel>
        </DropdownContainer>
      )}
      {error && (
        <div
          role="alert"
          className="theme-font-sm color-theme-red-500 theme-form-error-message"
        >
          {error?.message}
        </div>
      )}
    </HierarchicalMultiSelectWrapper>
  );
};

export default LocationHierarchySelect;
